import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Pagination from '@mui/material/Pagination';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { TOrder } from '@onesource/schemas';
import { getSession, useGetMemberOrdersQuery } from '@onesource/services';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { HiArrowRight, HiCreditCard, HiShoppingBag } from 'react-icons/hi2';

import { centsToUSD, formatDate, statusColor } from '../../utils';
import { BillingOrdersPreloader } from './orders-preloader';

function OrderRow({ order }: { order: TOrder }) {
  const paymentUrl = order.paymentUrl ?? undefined;

  return (
    <TableRow
      hover
      sx={{
        '&:last-child td': { border: 0 },
        transition: 'background 0.15s',
      }}
    >
      <TableCell>
        <Link to="/orders/$orderNo" params={{ orderNo: String(order.orderNo) }} style={{ textDecoration: 'none' }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: 'error.main', '&:hover': { textDecoration: 'underline' } }}
          >
            #{order.orderNo}
          </Typography>
        </Link>
      </TableCell>

      <TableCell sx={{ maxWidth: 300 }}>
        {order.products.map(p => (
          <Typography key={p.productId} variant="body2" color="text.secondary">
            {p.productName}
          </Typography>
        ))}
      </TableCell>

      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatDate(order.date)}
        </Typography>
      </TableCell>

      <TableCell>
        <Chip
          label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
          size="small"
          variant="outlined"
          color={statusColor(order.status)}
          sx={{ fontWeight: 500, fontSize: 12 }}
        />
      </TableCell>

      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {centsToUSD(order.total)}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {order.products.length} {order.products.length === 1 ? 'item' : 'items'}
          </Typography>
        </Box>
      </TableCell>

      <TableCell align="right">
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          {paymentUrl && (
            <Button
              component="a"
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="small"
              color="primary"
              endIcon={<HiCreditCard size={16} />}
              sx={{
                minWidth: 70,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                '& .MuiButton-endIcon': { marginRight: 0 },
              }}
            >
              Pay
            </Button>
          )}
          <Link to="/orders/$orderNo" params={{ orderNo: String(order.orderNo) }} style={{ textDecoration: 'none' }}>
            <Button
              variant="outlined"
              size="small"
              color="error"
              endIcon={<HiArrowRight size={16} />}
              sx={{
                minWidth: 76,
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                '& .MuiButton-endIcon': { marginRight: 0 },
              }}
            >
              View
            </Button>
          </Link>
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function OrdersTab() {
  const token = getSession();
  const [page, setPage] = useState(1);
  const perPage = 10;

  const { data, isLoading } = useGetMemberOrdersQuery({ page, perPage, enabled: !!token });

  if (isLoading) {
    return <BillingOrdersPreloader rows={perPage} />;
  }

  if (!data?.items?.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
        <HiShoppingBag size={56} style={{ opacity: 0.3 }} />
        <Typography variant="h6" color="text.secondary">
          No orders yet
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={380}>
          Your order history will appear here once you make a purchase.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3 }}>
      <Box sx={{ borderRadius: 2, border: theme => `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: { xs: 'calc(100vh - 280px)', md: 'calc(100vh - 350px)' }, overflowY: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'grey.200' }}>Order</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'grey.200', width: 300 }}>
                  Product
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'grey.200' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'grey.200' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.primary', bgcolor: 'grey.200' }}>Total</TableCell>
                <TableCell sx={{ bgcolor: 'grey.200' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map(order => (
                <OrderRow key={order.orderNo} order={order} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            px: 2,
            py: 1.5,
            borderTop: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Pagination
            count={data.totalPages || 1}
            page={page}
            onChange={(_, value) => setPage(value)}
            variant="outlined"
            color="primary"
            shape="rounded"
            size="large"
          />
        </Box>
      </Box>
    </Box>
  );
}
